"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export type BackendStatus = "ok" | "degraded" | "down" | "checking";

export interface BackendHealthState {
  /** Current status of the backend API */
  status: BackendStatus;
  /** Milliseconds since the last successful heartbeat (null if never succeeded) */
  lastOkAgo: number | null;
  /** ISO timestamp of last successful heartbeat */
  lastOkAt: string | null;
  /** Round-trip latency of the most recent probe */
  latencyMs: number | null;
  /** Human-readable hint when down */
  hint: string | null;
  /** Whether the backend just recovered (was down, now ok) */
  recovered: boolean;
  /** Force an immediate probe */
  checkNow: () => void;
}

const POLL_OK_MS = 30_000; // every 30s when healthy
const POLL_DOWN_MS = 3_000; // every 3s when down (fast retry for watchdog restarts)
const POLL_DEGRADED_MS = 15_000; // every 15s when degraded
const DOWN_GRACE_MS = 20_000; // minimum uptime window before declaring "down"
const DOWN_CONSECUTIVE_THRESHOLD = 3;
const OK_CONSECUTIVE_THRESHOLD = 2;

export function useBackendHealth(): BackendHealthState {
  const [status, setStatus] = useState<BackendStatus>("checking");
  const [lastOkAt, setLastOkAt] = useState<string | null>(null);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [recovered, setRecovered] = useState(false);
  const prevStatusRef = useRef<BackendStatus>("checking");
  const lastUpAtRef = useRef<number | null>(null);
  const consecutiveDownRef = useRef(0);
  const consecutiveOkRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const probe = useCallback(async () => {
    try {
      const res = await fetch("/api/heartbeat", { cache: "no-store" });
      const body = await res.json();

      if (!mountedRef.current) return;

      const rawStatus: BackendStatus =
        body.status === "ok"
          ? "ok"
          : body.status === "degraded"
            ? "degraded"
            : "down";

      const now = Date.now();
      const observedUp = rawStatus === "ok" || rawStatus === "degraded";
      if (observedUp) {
        lastUpAtRef.current = now;
        consecutiveDownRef.current = 0;
        if (rawStatus === "ok") {
          consecutiveOkRef.current += 1;
        } else {
          consecutiveOkRef.current = 0;
        }
      } else {
        consecutiveDownRef.current += 1;
        consecutiveOkRef.current = 0;
      }

      const withinGrace =
        lastUpAtRef.current !== null &&
        now - lastUpAtRef.current < DOWN_GRACE_MS;

      let nextStatus: BackendStatus = rawStatus;
      if (rawStatus === "down") {
        if (
          withinGrace ||
          consecutiveDownRef.current < DOWN_CONSECUTIVE_THRESHOLD
        ) {
          nextStatus = "degraded";
        }
      } else if (rawStatus === "ok") {
        if (
          prevStatusRef.current !== "ok" &&
          consecutiveOkRef.current < OK_CONSECUTIVE_THRESHOLD
        ) {
          nextStatus = "degraded";
        } else {
          nextStatus = "ok";
        }
      } else {
        nextStatus = "degraded";
      }

      // Detect recovery: was down or checking, now ok/degraded
      const wasDown =
        prevStatusRef.current === "down" ||
        prevStatusRef.current === "checking";
      const isUp = nextStatus === "ok" || nextStatus === "degraded";

      if (wasDown && isUp && prevStatusRef.current !== "checking") {
        setRecovered(true);
        setTimeout(() => {
          if (mountedRef.current) setRecovered(false);
        }, 8000);
      }

      prevStatusRef.current = nextStatus;
      setStatus(nextStatus);
      setLatencyMs(body.ms ?? null);
      setHint(nextStatus === "down" ? body.hint ?? null : null);

      if (observedUp) {
        setLastOkAt(body.ts ?? new Date().toISOString());
      }
    } catch {
      if (!mountedRef.current) return;
      const now = Date.now();
      consecutiveDownRef.current += 1;
      consecutiveOkRef.current = 0;
      const withinGrace =
        lastUpAtRef.current !== null &&
        now - lastUpAtRef.current < DOWN_GRACE_MS;
      const nextStatus: BackendStatus =
        withinGrace || consecutiveDownRef.current < DOWN_CONSECUTIVE_THRESHOLD
          ? "degraded"
          : "down";
      prevStatusRef.current = nextStatus;
      setStatus(nextStatus);
      setLatencyMs(null);
      setHint(
        nextStatus === "down"
          ? "Unable to reach heartbeat endpoint."
          : null,
      );
    }
  }, []);

  const scheduleNext = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const interval =
      prevStatusRef.current === "down"
        ? POLL_DOWN_MS
        : prevStatusRef.current === "degraded"
          ? POLL_DEGRADED_MS
          : POLL_OK_MS;
    timerRef.current = setTimeout(async () => {
      await probe();
      if (mountedRef.current) scheduleNext();
    }, interval);
  }, [probe]);

  const checkNow = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    void probe().then(() => {
      if (mountedRef.current) scheduleNext();
    });
  }, [probe, scheduleNext]);

  useEffect(() => {
    mountedRef.current = true;
    void probe().then(() => scheduleNext());
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [probe, scheduleNext]);

  const lastOkAgo =
    lastOkAt !== null
      ? Math.round((Date.now() - new Date(lastOkAt).getTime()) / 1000) * 1000
      : null;

  return { status, lastOkAgo, lastOkAt, latencyMs, hint, recovered, checkNow };
}
